import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import api from './utils/api';
import {
  startLogin,
  exchangeCodeForToken,
  logoutRedirect,
} from './utils/oidc';
import { isAuthentikConfigured } from './authentikConfig';

const AuthContext = createContext(null);

const TOKEN_KEY = 'oidc_access_token';
const USER_KEY = 'oidc_user';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    const saved = localStorage.getItem(USER_KEY);
    if (!saved) return null;
    try {
      return JSON.parse(saved);
    } catch {
      localStorage.removeItem(USER_KEY);
      return null;
    }
  });
  const [loading, setLoading] = useState(true);

  const refreshUser = useCallback(async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      setUser(null);
      return null;
    }
    try {
      const res = await api.get('/users/me');
      setUser(res.data);
      localStorage.setItem(USER_KEY, JSON.stringify(res.data));
      return res.data;
    } catch (err) {
      console.error('取得使用者資料失敗:', err);
      if (err?.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(USER_KEY);
        setUser(null);
      }
      return null;
    }
  }, []);

  useEffect(() => {
    refreshUser().finally(() => setLoading(false));
  }, [refreshUser]);

  const login = () => {
    if (!isAuthentikConfigured()) {
      alert('Authentik OIDC 尚未設定，請先在 src/authentikConfig.js 填入連線資訊。');
      return;
    }
    startLogin().catch((err) => {
      console.error('啟動登入失敗:', err);
      alert(err.message || '啟動登入失敗');
    });
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    setUser(null);
    logoutRedirect();
  };

  const handleCallback = async (code, state) => {
    const tokenRes = await exchangeCodeForToken(code, state);
    const accessToken = tokenRes.access_token;
    if (!accessToken) throw new Error('OIDC 回應缺少 access_token');
    localStorage.setItem(TOKEN_KEY, accessToken);
    return await refreshUser();
  };

  const value = {
    user,
    loading,
    isAuthenticated: !!user,
    isAdmin: Boolean(user?.is_admin),
    isOidcReady: isAuthentikConfigured(),
    login,
    logout,
    handleCallback,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth 必須在 AuthProvider 內使用');
  return context;
};

export default AuthContext;
